import React from 'react';
import styled from 'styled-components';
import Navbar from './Navbar';
import DesktopMenu from './DesktopMenu';
import Footer from './Footer';
import coverImg from '../images/strategy_gr.png';

const GUMROAD_URL = 'https://spectralcenter.gumroad.com/l/strategy';

const StrategyOfFutureness = () => {
    return (
        <>
            <Navbar/>
            <DeskWrap>
                <DesktopMenu/>
                <Wrapper>
                    <Body>
                        <h1>The Strategy of Futureness</h1>
                        <Sub>A Four-Week Workshop in Hypnosis and Visionary Imagination</Sub>
                        <Price>€111</Price>
                        <CoverImage src={coverImg} alt="The Strategy of Futureness" />

                        <p>Most future-planning starts from where you are now and tries to reason forward — which usually just extends the present a little further out, dressed up as a plan. The subconscious works differently. It can meet a future self directly, the way it meets a memory: not as an idea to think about, but as something to actually stand inside.</p>

                        <p>This workshop puts two disciplines to work on that difference. Hypnosis gives direct access to the subconscious. The craft tools of visionary fiction — worldbuilding, narrative structure — give that access something to build with. Writers like Ursula K. Le Guin and Octavia Butler treated imagining otherwise as serious labor, not escapism. This workshop borrows that seriousness and applies it to your own future self.</p>

                        <p>I'm an artist and a hypnotherapist. My work sits at the place where trance, sound, and creative practice meet, and this workshop is where that practice turns toward the future instead of the present.</p>

                        <h3>The method</h3>
                        <p>Hypnosis and self-hypnosis to access the subconscious directly. Somatic practice to anchor what comes up in the body, not just the mind. And the craft tools of visionary fiction and worldbuilding to give the whole process a shape — the Hero's Journey and the Enneagram as two structural maps for a process you can repeat on your own, long after the four weeks end.</p>

                        <h3>This is for you if</h3>
                        <p>Fear, doubt, or an old story about who you are keeps overriding what you actually want. You're ready to build a real vision for where you're headed, rather than just react to where you already are. Storytelling and worldbuilding as tools for self-work interest you more than another five-step plan. And you want practices you'll actually keep — not a bundle of ideas you meant to start.</p>

                        <h3>How it works</h3>
                        <p>One weekly session, 60 to 90 minutes, pre-recorded — part lecture, part guided hypnosis. Daily practice of five to ten minutes: self-hypnosis, creative writing, somatic exercise. Homework woven through the four weeks: writing prompts, recordings and meditations, and small rituals to integrate what's coming up.</p>

                        <h3>The four weeks</h3>

                        <h4>Week 1 — The Call to Adventure</h4>
                        <p>Vision and intention: who you are, and what future you're actually ready to step into. We start by examining the story you're currently living inside, and build the foundational tools — self-hypnosis, visualization — you'll use for the rest of the workshop.</p>

                        <h4>Week 2 — Crossing the Threshold</h4>
                        <p>Confronting fear directly, using childhood regression hypnosis to release patterns that started long before the current story did. This is also where you build the daily ritual that anchors your vision going forward.</p>

                        <h4>Week 3 — Clear the Path</h4>
                        <p>Clearing hypnosis to work with resistance directly. We turn to visionary fiction technique here — using the tools writers use to build believable worlds to imagine your own future self living boldly, in detail.</p>

                        <h4>Week 4 — Embody the Vision</h4>
                        <p>Opening to the transformation itself, with trust rather than force. The workshop closes with you presenting your future vision back to yourself — a way of making it real enough to carry forward.</p>

                        <h3>What tends to shift</h3>
                        <p>Expect some movement in how you see your own possibilities from the first week on. By the end, you'll have an actual built vision of a future self — not just an intention toward one — along with a set of daily practices you already know how to use.</p>

                        <BuyButton href={GUMROAD_URL} target="_blank" rel="noopener noreferrer">Join the Workshop</BuyButton>
                    </Body>
                </Wrapper>
            </DeskWrap>
            <Footer/>
        </>
    )
}

const DeskWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;

    @media (min-width: 1000px) {
        flex-direction: row;
    }
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 90vh;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;

    @media (min-width: 1000px) {
        padding: 2rem;
    }
`

const Body = styled.div`
    font-family: var(--font-body);
    width: 95%;
    padding: 0 1rem;
    max-width: 100%;

    h1 {
        font-size: 1.8rem;
        margin-bottom: 0.4rem;
        color: #333;
    }

    h3 {
        font-size: 1.2rem;
        margin: 1.75rem 0 0.75rem;
        color: #333;
    }

    h4 {
        font-size: 1.05rem;
        margin: 1.25rem 0 0.5rem;
        color: #333;
    }

    p {
        line-height: 1.7;
        margin-bottom: 1.1rem;
        color: #444;
    }

    @media (min-width: 1000px) {
        max-width: 65ch;
        padding: 0 1rem;
    }
`

const Sub = styled.p`
    font-style: italic;
    font-size: 1.05rem;
    color: #666;
    margin: 0 0 0.75rem;
`

const Price = styled.p`
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 1.25rem;
`

const CoverImage = styled.img`
    width: 100%;
    max-width: 480px;
    display: block;
    margin: 0 0 1.75rem;
    transform: rotate(-1deg);
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
`

const BuyButton = styled.a`
    display: inline-block;
    margin-top: 1rem;
    padding: 0.85rem 1.75rem;
    background: #333;
    color: #fff;
    text-decoration: none;
    border-radius: 0.4rem;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: #000;
    }
`

export default StrategyOfFutureness;